/**
 * HowToGuideDialog — 6-page AAA-quality "How To Play" slideshow.
 * Auto-opens on first login; accessible from hamburger menu.
 * Two-column: left = title + icon bullets + hint, right = framed illustration with gradient blend.
 */

import Phaser from 'phaser';
import {
  Colors, CSSColors, Fonts, Space, Depth, Radius,
  createDialogChrome, createButton, closeDialog,
} from './GameUITheme';
import { I18n } from '../../i18n/I18nService';
import { isMobile } from '../../utils/ResponsiveHelper';

const STORAGE_KEY = 'crimsonmandate_how_to_seen';
const PAGE_COUNT = 6;

const IMG_CORNER = Radius.lg;

const PAGE_IMAGES = [
  'howto-page1', 'howto-page2', 'howto-page3',
  'howto-page4', 'howto-page5', 'howto-page6',
];

// Themed bullet icons per page (small unicode symbols)
const BULLET_ICONS: [string, string][] = [
  ['\u2726', '\u2726'],   // p1: four-pointed star
  ['\u25CE', '\u26A0'],   // p2: bullseye (radar), warning (danger)
  ['\u26CF', '\u2693'],   // p3: pick (mine), anchor (dock)
  ['\u2694', '\u2620'],   // p4: crossed swords (attack), skull (plunder)
  ['\u2691', '\u2605'],   // p5: banner (alliance), star (rewards)
  ['\u2737', '\u269B'],   // p6: eight-pointed star (relic), atom (unknown)
];

export class HowToGuideDialog {
  private container: Phaser.GameObjects.Container;
  private scene: Phaser.Scene;
  private dw: number;
  private dh: number;
  private contentLeft: number;
  private contentTop: number;
  private textWrap: number;
  private imgFrameX: number;
  private imgFrameY: number;
  private imgFrameW: number;
  private imgFrameH: number;
  private currentPage = 0;
  private pageContainers: Phaser.GameObjects.Container[] = [];
  private dots: Phaser.GameObjects.Graphics[] = [];
  private dotZones: Phaser.GameObjects.Zone[] = [];
  private backBtn: Phaser.GameObjects.Container | null = null;
  private nextBtn: Phaser.GameObjects.Container | null = null;
  private skipBtn: Phaser.GameObjects.Container | null = null;
  private isFirstRun: boolean;
  private pageImages: Phaser.GameObjects.Image[] = [];

  constructor(scene: Phaser.Scene, firstRun = false) {
    this.scene = scene;
    this.isFirstRun = firstRun;

    const mobile = isMobile(scene);
    const dw = mobile ? 340 : 720;
    const dh = mobile ? 540 : 450;
    this.dw = dw;
    this.dh = dh;

    // Layout constants derived from dialog size and mobile state
    this.contentLeft = mobile ? 16 : 32;
    this.contentTop = 62;
    this.textWrap = mobile ? dw - this.contentLeft * 2 : dw * 0.42;
    this.imgFrameX = mobile ? dw + 100 : dw * 0.50 + 8;
    this.imgFrameY = 56;
    this.imgFrameW = mobile ? 0 : dw * 0.50 - 24;
    this.imgFrameH = mobile ? 0 : dh - 110;

    const { width, height } = scene.cameras.main;
    const cx = Math.round((width - dw) / 2);
    const cy = Math.round((height - dh) / 2);

    this.container = scene.add.container(cx, cy);
    this.container.setDepth(Depth.dialog);

    createDialogChrome(scene, this.container, dw, dh, {
      onClose: () => this.close(),
      title: I18n.t('howto.title'),
    });

    this.buildPages();
    this.buildNavigation();
    this.showPage(0, false);
  }

  // ── Helpers ─────────────────────────────────────────────────

  private addTitle(page: Phaser.GameObjects.Container, text: string): void {
    const title = this.scene.add.text(this.contentLeft, this.contentTop, text, {
      fontFamily: Fonts.heading,
      fontSize: '22px',
      fontStyle: '900',
      color: CSSColors.cyan,
      wordWrap: { width: this.textWrap },
    });
    page.add(title);
  }

  private addBullets(
    page: Phaser.GameObjects.Container,
    b1: string, b2: string,
    icons: [string, string],
    startY = this.contentTop + 48,
  ): void {
    const style: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: Fonts.body,
      fontSize: '15px',
      color: '#c0d8ee',
      wordWrap: { width: this.textWrap - 24 },
      lineSpacing: 6,
    };

    const iconStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontFamily: Fonts.heading,
      fontSize: '14px',
      color: CSSColors.cyan,
    };

    const icon1 = this.scene.add.text(this.contentLeft, startY + 2, icons[0], iconStyle);
    const bullet1 = this.scene.add.text(this.contentLeft + 22, startY, b1, style);

    const icon2 = this.scene.add.text(this.contentLeft, startY + bullet1.height + 20, icons[1], iconStyle);
    const bullet2 = this.scene.add.text(this.contentLeft + 22, startY + bullet1.height + 18, b2, style);

    page.add([icon1, bullet1, icon2, bullet2]);
  }

  /**
   * Draw image with frame treatment: rounded mask, 1px cyan stroke,
   * inner shadow, and a left-to-right gradient blend.
   */
  private addPageImage(page: Phaser.GameObjects.Container, key: string, index: number): void {
    if (!this.scene.textures.exists(key)) return;

    const fx = this.imgFrameX;
    const fy = this.imgFrameY;
    const fw = this.imgFrameW;
    const fh = this.imgFrameH;
    const r = IMG_CORNER;

    // Image (cover-crop: scale uniformly to fill frame, mask clips overflow)
    const img = this.scene.add.image(fx + fw / 2, fy + fh / 2, key);
    const srcW = img.width;
    const srcH = img.height;
    const coverScale = Math.max(fw / srcW, fh / srcH);
    img.setScale(coverScale);

    // Rounded rectangle mask
    const maskShape = this.scene.add.graphics();
    maskShape.setVisible(false);
    maskShape.fillStyle(0xffffff);
    maskShape.fillRoundedRect(
      this.container.x + fx,
      this.container.y + fy,
      fw, fh, r,
    );
    img.setMask(maskShape.createGeometryMask());

    page.add(img);
    this.pageImages[index] = img;

    // Gradient overlay (left-to-right dark blend so art merges into panel)
    const gradient = this.scene.add.graphics();
    const gradW = Math.round(fw * 0.35);
    const steps = 20;
    for (let s = 0; s < steps; s++) {
      const a = 0.20 * (1 - s / steps);
      gradient.fillStyle(0x131026, a);
      const sx = fx + Math.round((gradW / steps) * s);
      const sw = Math.ceil(gradW / steps) + 1;
      gradient.fillRect(sx, fy, sw, fh);
    }
    page.add(gradient);

    // Inner shadow (subtle vignette on all edges)
    const shadow = this.scene.add.graphics();
    shadow.fillStyle(0x000000, 0.12);
    shadow.fillRoundedRect(fx, fy, fw, 6, { tl: r, tr: r, bl: 0, br: 0 });
    shadow.fillRoundedRect(fx, fy + fh - 6, fw, 6, { tl: 0, tr: 0, bl: r, br: r });
    page.add(shadow);

    // 1px cyan stroke + corner radius
    const frame = this.scene.add.graphics();
    frame.lineStyle(1, Colors.cyan, 0.3);
    frame.strokeRoundedRect(fx, fy, fw, fh, r);
    page.add(frame);
  }

  // ── Pages ───────────────────────────────────────────────────

  private buildPages(): void {
    for (let i = 0; i < PAGE_COUNT; i++) {
      const page = this.scene.add.container(0, 0);
      page.setAlpha(0);
      this.container.add(page);

      switch (i) {
        case 0: this.buildPage1(page); break;
        case 1: this.buildPage2(page); break;
        case 2: this.buildPage3(page); break;
        case 3: this.buildPage4(page); break;
        case 4: this.buildPage5(page); break;
        case 5: this.buildPage6(page); break;
      }

      this.addPageImage(page, PAGE_IMAGES[i], i);
      this.pageContainers.push(page);
    }
  }

  private buildPage1(page: Phaser.GameObjects.Container): void {
    this.addTitle(page, I18n.t('howto.page1_title'));
    this.addBullets(page, I18n.t('howto.page1_bullet1'), I18n.t('howto.page1_bullet2'), BULLET_ICONS[0]);
  }

  private buildPage2(page: Phaser.GameObjects.Container): void {
    this.addTitle(page, I18n.t('howto.page2_title'));
    this.addBullets(page, I18n.t('howto.page2_bullet1'), I18n.t('howto.page2_bullet2'), BULLET_ICONS[1]);
  }

  private buildPage3(page: Phaser.GameObjects.Container): void {
    this.addTitle(page, I18n.t('howto.page3_title'));
    this.addBullets(page, I18n.t('howto.page3_bullet1'), I18n.t('howto.page3_bullet2'), BULLET_ICONS[2]);
  }

  private buildPage4(page: Phaser.GameObjects.Container): void {
    this.addTitle(page, I18n.t('howto.page4_title'));
    this.addBullets(page, I18n.t('howto.page4_bullet1'), I18n.t('howto.page4_bullet2'), BULLET_ICONS[3]);
  }

  private buildPage5(page: Phaser.GameObjects.Container): void {
    this.addTitle(page, I18n.t('howto.page5_title'));
    this.addBullets(page, I18n.t('howto.page5_bullet1'), I18n.t('howto.page5_bullet2'), BULLET_ICONS[4]);
  }

  private buildPage6(page: Phaser.GameObjects.Container): void {
    this.addTitle(page, I18n.t('howto.page6_title'));
    this.addBullets(page, I18n.t('howto.page6_bullet1'), I18n.t('howto.page6_bullet2'), BULLET_ICONS[5]);
  }

  // ── Navigation ──────────────────────────────────────────────

  private buildNavigation(): void {
    const btnY = this.dh - 28;
    const btnCenterY = btnY - 14 + 14; // vertical center of 28px-tall buttons

    // Back button
    this.backBtn = createButton(this.scene, Space.xl, btnY - 14, I18n.t('howto.back'), {
      width: 70, height: 28,
      onClick: () => this.prevPage(),
    });
    this.container.add(this.backBtn);

    // Skip button (first run only)
    if (this.isFirstRun) {
      this.skipBtn = createButton(this.scene, Space.xl + 80, btnY - 14, I18n.t('howto.skip'), {
        width: 60, height: 28,
        onClick: () => this.close(),
      });
      this.container.add(this.skipBtn);
    }

    // Next / CTA button (bottom-right)
    this.nextBtn = createButton(this.scene, this.dw - Space.xl - 90, btnY - 14, I18n.t('howto.next'), {
      width: 90, height: 28,
      onClick: () => this.onNextClick(),
    });
    this.container.add(this.nextBtn);

    // Page dots (clickable, centered between buttons, same Y as button centers)
    const dotSpacing = 18;
    const dotsWidth = PAGE_COUNT * dotSpacing;
    const dotsStartX = this.dw / 2 - dotsWidth / 2;

    for (let i = 0; i < PAGE_COUNT; i++) {
      const dot = this.scene.add.graphics();
      const dx = dotsStartX + i * dotSpacing + dotSpacing / 2;
      dot.setPosition(dx, btnCenterY);
      this.container.add(dot);
      this.dots.push(dot);

      const zone = this.scene.add.zone(dx - 8, btnCenterY - 8, 16, 16).setOrigin(0, 0);
      zone.setInteractive(
        new Phaser.Geom.Rectangle(0, 0, 16, 16),
        Phaser.Geom.Rectangle.Contains,
      );
      const idx = i;
      zone.on('pointerdown', () => this.showPage(idx, true));
      zone.on('pointerover', () => this.scene.input.setDefaultCursor('pointer'));
      zone.on('pointerout', () => this.scene.input.setDefaultCursor('default'));
      this.container.add(zone);
      this.dotZones.push(zone);
    }

    this.updateNav();
  }

  private updateNav(): void {
    const isLast = this.currentPage === PAGE_COUNT - 1;
    const isFirst = this.currentPage === 0;

    // Update dots
    this.dots.forEach((dot, i) => {
      dot.clear();
      if (i === this.currentPage) {
        dot.fillStyle(Colors.cyan, 1);
        dot.fillCircle(0, 0, 5);
      } else {
        dot.fillStyle(Colors.cyanDim, 0.35);
        dot.fillCircle(0, 0, 3.5);
      }
    });

    if (this.backBtn) {
      this.backBtn.setVisible(!isFirst);
    }

    if (this.skipBtn) {
      this.skipBtn.setVisible(!isLast);
    }

    // Update Next / CTA label
    if (this.nextBtn) {
      const label = this.nextBtn.getAt(1) as Phaser.GameObjects.Text;
      if (label && 'setText' in label) {
        label.setText(isLast ? I18n.t('howto.launch') : I18n.t('howto.next'));
      }
    }
  }

  private onNextClick(): void {
    if (this.currentPage < PAGE_COUNT - 1) {
      this.showPage(this.currentPage + 1, true);
    } else {
      this.close();
    }
  }

  private prevPage(): void {
    if (this.currentPage > 0) {
      this.showPage(this.currentPage - 1, true);
    }
  }

  private showPage(index: number, animate: boolean): void {
    if (index === this.currentPage && animate) return;

    const oldPage = this.pageContainers[this.currentPage];
    const newPage = this.pageContainers[index];
    const goingForward = index > this.currentPage;
    const slideOffset = 30;

    if (animate && oldPage !== newPage) {
      // Slide + fade old page out
      this.scene.tweens.add({
        targets: oldPage,
        alpha: 0,
        x: goingForward ? -slideOffset : slideOffset,
        duration: 200,
        ease: 'Quad.easeIn',
        onComplete: () => oldPage.setPosition(0, 0),
      });

      // Slide + fade new page in (from opposite side)
      newPage.setPosition(goingForward ? slideOffset : -slideOffset, 0);
      newPage.setAlpha(0);
      this.scene.tweens.add({
        targets: newPage,
        alpha: 1,
        x: 0,
        duration: 220,
        delay: 60,
        ease: 'Quad.easeOut',
      });

      // Parallax: image slides slightly slower for depth
      const newImg = this.pageImages[index];
      if (newImg) {
        const imgCenter = this.imgFrameX + this.imgFrameW / 2;
        newImg.x = imgCenter + (goingForward ? 15 : -15);
        this.scene.tweens.add({
          targets: newImg,
          x: imgCenter,
          duration: 300,
          delay: 60,
          ease: 'Quad.easeOut',
        });
      }
    } else {
      oldPage.setAlpha(0);
      oldPage.setPosition(0, 0);
      newPage.setAlpha(1);
      newPage.setPosition(0, 0);
    }

    this.currentPage = index;
    this.updateNav();
  }

  private close(): void {
    localStorage.setItem(STORAGE_KEY, 'true');
    closeDialog(this.scene, this.container);
  }

  // ── Static helpers ──────────────────────────────────────────

  static hasBeenSeen(): boolean {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  }
}
